import { apiClient } from "@/shared/api/client";
import type {
  BalanceResponse,
  CatchResponse,
  CharacterResponse,
  LeaderboardClubsResponse,
  LeaderboardResponse,
  MarketplaceResponse,
  MembersResponse,
  PendingPeriodsResponse,
  PeriodClaimResponse,
  QuoteResponse,
  MyHabitsList,
  StatLeaderboardResponse,
  SubscribeResponse,
  TodayResponse,
  TopupResponse,
  WalletResponse,
} from "@/shared/types";

export const marketplaceApi = {
  list: () => apiClient.get<MarketplaceResponse>("/marketplace").then((r) => r.data),
};

export const habitsApi = {
  today: (habitId: string) =>
    apiClient.get<TodayResponse>(`/habits/${habitId}/today`).then((r) => r.data),
  join: (habitId: string) =>
    apiClient.post<{ ok: boolean }>(`/habits/${habitId}/join`).then((r) => r.data),
  // Feature/paused-member-ux: возвращает ACTIVE + PAUSED клубы с
  // membership_status / subscription_until. См. MyHabit в types/index.ts.
  mine: () => apiClient.get<MyHabitsList>("/me/habits").then((r) => r.data),
};

export const membersApi = {
  list: (habitId: string) =>
    apiClient.get<MembersResponse>(`/habits/${habitId}/members`).then((r) => r.data),
  catch: (habitId: string, violatorMembershipId: string) =>
    apiClient
      .post<CatchResponse>(`/habits/${habitId}/catch`, {
        violator_membership_id: violatorMembershipId,
      })
      .then((r) => r.data),
};

export const balanceApi = {
  get: () => apiClient.get<BalanceResponse>("/balance").then((r) => r.data),
  topup: (payload: { habit_id: string; amount_fp: number }) =>
    apiClient.post<TopupResponse>("/payments/topup", payload).then((r) => r.data),
  /**
   * Flow Point: оплата подписки (скидка по FP, +500 FP, период 30 дней).
   * Сумму скидки считает сервер — клиент присылает только факт согласия
   * и `idempotency_key` (защита от двойного списания).
   */
  subscribe: (payload: {
    habit_id: string;
    subscription_accepted: boolean;
    idempotency_key: string;
  }) =>
    apiClient
      .post<SubscribeResponse>("/payments/subscribe", payload)
      .then((r) => r.data),
  /**
   * Flow Point (этап 2, часть 3): продление подписки в окне.
   *
   * Тело и ответ — те же, что у `subscribe` (один контракт на два
   * эндпоинта); различается только `outcome` (`renewed`).
   */
  renew: (payload: {
    habit_id: string;
    subscription_accepted: boolean;
    idempotency_key: string;
  }) =>
    apiClient
      .post<SubscribeResponse>("/payments/renew", payload)
      .then((r) => r.data),
  /**
   * Flow Point: предпросмотр оплаты — серверный расчёт (цена, скидка по FP,
   * к оплате, бонус). Ничего не меняет. `outcome` говорит, что предложить:
   * оплата / подписка уже активна / бесплатное вступление.
   */
  quote: (habitId: string) =>
    apiClient
      .get<QuoteResponse>("/payments/quote", { params: { habit_id: habitId } })
      .then((r) => r.data),
};

/** Pravki-deposit-sse.md §Z-4.1: глобальный кошелёк юзера. */
export const walletApi = {
  get: () => apiClient.get<WalletResponse>("/me/wallet").then((r) => r.data),
};

/**
 * Этап 3: награды за закрытые периоды и офферы «Перезапуск».
 * Суммы считает сервер — клиент их не присылает.
 */
export const periodsApi = {
  pending: () =>
    apiClient.get<PendingPeriodsResponse>("/periods/pending").then((r) => r.data),
  claim: (periodId: string) =>
    apiClient
      .post<PeriodClaimResponse>(`/periods/${periodId}/claim`)
      .then((r) => r.data),
};

export const leaderboardApi = {
  streaks: (habitId: string) =>
    apiClient
      .get<LeaderboardResponse>(`/habits/${habitId}/leaderboard/streak`)
      .then((r) => r.data),
  catchers: (habitId: string) =>
    apiClient
      .get<LeaderboardResponse>(`/habits/${habitId}/leaderboard/catches`)
      .then((r) => r.data),
  shame: (habitId: string) =>
    apiClient
      .get<LeaderboardResponse>(`/habits/${habitId}/leaderboard/shame`)
      .then((r) => r.data),
  clubs: (tab: "streak" | "catches" | "shame") =>
    apiClient
      .get<LeaderboardClubsResponse>(`/leaderboard/${tab}/clubs`)
      .then((r) => r.data),
  /**
    * Phase 3 v2 Task 3.9: per-habit лидерборд по характеристике клуба.
    * Endpoint: GET /habits/{habit_id}/leaderboard (per-habit, НЕ /leaderboard/stat).
    * Backend fallback: если habit.stat_definition_id IS NULL →
    * { items: [], total: null } — не ошибка, фича не активирована админом.
    */
  stat: (habitId: string) =>
    apiClient
      .get<StatLeaderboardResponse>(`/habits/${habitId}/leaderboard`)
      .then((r) => r.data),
};

/**
 * Phase 3 v2 Task 3.9: глобальная карточка персонажа.
 * Источник для CharacterPage (status + frozen stats + LevelUp detection).
 * Не сбрасываем кэш между рендерами — CharacterService на бэке фильтрует
 * stats при каждом запросе (см. MIN_STAT_VALUE_TO_SHOW).
 */
export const characterApi = {
  get: () =>
    apiClient.get<CharacterResponse>("/character/me").then((r) => r.data),
};
